"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogPrimitiveContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface VsLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible title, announced to screen readers (visually hidden). */
  title: string;
  /** Optional prev/next handlers — render arrow controls + bind arrow keys. */
  onPrev?: () => void;
  onNext?: () => void;
  children: ReactNode;
  className?: string;
}

const NAV_BTN =
  "absolute top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

/**
 * Shared near-fullscreen dark lightbox for the Visionaries Summit media
 * (gallery photos, clip/recap videos). Built on the shadcn/Radix `Dialog`
 * primitives, so focus trapping, focus restoration, Escape-to-close, body
 * scroll lock and ARIA wiring all come for free.
 */
export function VsLightbox({
  open,
  onOpenChange,
  title,
  onPrev,
  onNext,
  children,
  className,
}: VsLightboxProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[100] bg-black/90 backdrop-blur-sm" />
        <DialogPrimitiveContent
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") onPrev?.();
            else if (e.key === "ArrowRight") onNext?.();
          }}
          // Clicking the empty area (the content itself, not a child) closes.
          onClick={(e) => {
            if (e.target === e.currentTarget) onOpenChange(false);
          }}
          className={cn(
            "fixed inset-0 top-0 left-0 z-[100] flex max-w-none translate-x-0 translate-y-0",
            "items-center justify-center gap-0 rounded-none border-0 bg-transparent p-4 shadow-none",
            "max-md:p-2",
            className
          )}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>

          <DialogClose
            aria-label="Close"
            className="top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white opacity-100 hover:bg-white/20 focus:ring-white"
          />

          {onPrev ? (
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous"
              className={cn(NAV_BTN, "left-4 max-md:left-2")}
            >
              <ChevronLeft size={24} aria-hidden />
            </button>
          ) : null}

          {children}

          {onNext ? (
            <button
              type="button"
              onClick={onNext}
              aria-label="Next"
              className={cn(NAV_BTN, "right-4 max-md:right-2")}
            >
              <ChevronRight size={24} aria-hidden />
            </button>
          ) : null}
        </DialogPrimitiveContent>
      </DialogPortal>
    </Dialog>
  );
}
